import { useUpload } from "@/api/files";
import { CalculationForm } from "@/forms/calculation-form";
import { useCalculation } from "@/hooks/use-calculation";
import { client } from "@/hono";
import { useBreadcrumbTitle } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentBox } from "@msuite/ui/content-box";
import {
	FileUploadCollectorProvider,
	useFileUpload,
	useFileUploadCollector,
} from "@msuite/ui/file-upload";
import { useAutoSave } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type CalculationFormSchema, calculationFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { CalculationLockedAlert } from "../calculation-locked-alert";
import { CalculationEditorContext } from "./context";

export function CalculationEditor() {
	/** Hooks */
	const { t } = useTranslation();
	const params = useParams();

	/** State */
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [loadFailed, setLoadFailed] = useState<boolean>(false);
	const { uploadFn } = useUpload();
	const fileUploadController = useFileUpload({
		uploadFn,
		defaultFiles: async () => {
			const response = await client.files[":tableName"][":key"][":identifier"].$get({
				param: {
					tableName: "serviceCalculationFiles",
					key: "serviceCalculationId",
					identifier: params.calculationId || "",
				},
			});
			const data = await responseHandler(response);
			return data.files;
		},
	});
	const navigate = useNavigate();
	const collector = useFileUploadCollector();

	/** Query */
	const { calculation } = useCalculation(params.calculationId ?? "");

	/** Form */
	const methods = useForm({
		reValidateMode: "onChange",
		resolver: resolver(calculationFormSchema),
		defaultValues: async () => {
			// A rejected defaultValues promise leaves formState.isLoading true forever, so a
			// refused or failed load would spin indefinitely behind the error toast. The flag
			// switches the guard below to an honest answer instead — and the form must not mount
			// without values, because the auto-save would then persist an empty calculation.
			try {
				const calculationId = params.calculationId;
				if (!calculationId) throw new Error("Calculation ID is required");
				const response = await client.calculations[":calculationId"].form.$get({
					param: {
						calculationId,
					},
				});
				const data = await responseHandler(response);
				return data.form as CalculationFormSchema;
			} catch (error) {
				setLoadFailed(true);
				throw error;
			}
		},
	});

	/** Computed */
	// A locked calculation opens read-only: the server refuses every save with 409, so the
	// auto-save would only raise an error toast per keystroke.
	const isLocked = calculation?.state === "locked";

	useAutoSave(methods, () => {
		if (!isLocked) handleOnSubmit(false);
	});
	// The breadcrumb names the calculation as it is typed rather than as it was last read.
	const calculationName = useWatch({ control: methods.control, name: "name" });
	useBreadcrumbTitle(params.calculationId, calculationName);

	/** Functions */
	async function handleOnSubmit(withTrigger?: boolean) {
		if (isLocked) {
			handleOnReturn();
			return;
		}
		try {
			if (withTrigger) {
				setIsSubmitting(true);
				if (!(await methods.trigger())) {
					handleOnReject(methods.formState.errors);
					return;
				}
			}
			collector.loader.start();
			const uploadResults = await fileUploadController.handleOnFileUpload();
			const json = methods.getValues();
			const response = await client.calculations[":calculationId"].$post({
				param: {
					calculationId: guardString(params.calculationId),
				},
				json: {
					...json,
					uploadResults,
				},
			});
			const data = await responseHandler(response);
			await collector.handleOnBatchUpload();
			if (!data.success) throw new Error("Failed to create calculation");
			if (withTrigger) handleOnReturn();
		} catch (error) {
			console.error(error);
			toast.error(t("general.something_went_wrong"));
		} finally {
			setIsSubmitting(false);
		}
	}

	function handleOnReturn() {
		const returnTo = new URLSearchParams(window.location.search).get("returnTo");
		if (returnTo) navigate(decodeURIComponent(returnTo));
	}

	function handleOnReject(errors: typeof methods.formState.errors) {
		console.error(errors);
		toast.error(t("general.something_went_wrong"));
	}

	/** Guard */
	if (loadFailed) {
		return (
			<ContentBox>
				<div className="w-full h-full v-stack items-center justify-center gap-4">
					<BlankSlate
						withBorder={false}
						withBackground={false}
						label={t("calculations.calculation_not_loadable")}
					/>
					<Button
						variant="primary"
						onClick={() => navigate(-1)}
					>
						{t("general.back")}
					</Button>
				</div>
			</ContentBox>
		);
	}
	if (methods.formState.isLoading) {
		return (
			<ContentBox>
				<div className="w-full h-full gap">
					<BlankSlate
						isLoading
						withBorder={false}
						withAccessory={false}
						withBackground={false}
						label={t("calculations.preparing_calculation")}
					/>
				</div>
			</ContentBox>
		);
	}

	/** Render */
	return (
		<CalculationEditorContext.Provider
			value={{
				handleOnSubmit,
				isSubmitting,
				setIsSubmitting,
				fileUploadController,
				calculationId: guardString(params.calculationId),
				isLocked,
			}}
		>
			<FileUploadCollectorProvider collector={collector}>
				<FormProvider {...methods}>
					<ContentBox bottomPadding="none">
						{calculation && <CalculationLockedAlert calculation={calculation} />}
						<CalculationForm />
					</ContentBox>
				</FormProvider>
			</FileUploadCollectorProvider>
		</CalculationEditorContext.Provider>
	);
}
